import { supabase } from "../supabaseClient";

const FIXTURE_COLUMNS =
  "id, date, time, season, city, division, age_group, home_team_id, away_team_id, home_team_score, away_team_score, game_played, is_forfeit, home_team_scorers, away_team_scorers";

/** Season / city / division combinations that have soccer fixtures. */
export async function fetchInsightOptions() {
  const { data, error } = await supabase
    .from("fixtures")
    .select("season, city, division, age_group")
    .eq("sport", "Soccer");
  if (error) throw error;
  const seen = new Map();
  (data || []).forEach((r) => {
    if (!r.season || !r.city || !r.division) return;
    const key = `${r.season}|${r.city}|${r.division}`;
    if (!seen.has(key)) seen.set(key, { season: r.season, city: r.city, division: r.division, ageGroup: r.age_group });
  });
  const options = [...seen.values()];
  const seasons = [...new Set(options.map((o) => o.season))].sort(compareSeasons);
  return { options, seasons };
}

/** Newest season first: F26 > S26 > W26 > F25 … */
function compareSeasons(a, b) {
  const yr = (s) => Number(String(s).slice(-2)) || 0;
  const term = (s) => ({ W: 1, S: 2, SP: 2, SU: 3, F: 4 }[String(s).replace(/\d+$/, "").toUpperCase()] || 0);
  return yr(b) - yr(a) || term(b) - term(a);
}

/**
 * Everything one division report needs, in three queries:
 * fixtures for the season + city + division, the teams in them, and the scorers named in them.
 */
export async function fetchDivisionFixtures({ season, city, division }) {
  const { data, error } = await supabase
    .from("fixtures")
    .select(FIXTURE_COLUMNS)
    .eq("sport", "Soccer")
    .eq("season", season)
    .eq("city", city)
    .eq("division", division)
    .order("date", { ascending: true })
    .order("time", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchDivisionData({ season, city, division }) {
  const fixtures = await fetchDivisionFixtures({ season, city, division });

  const teamIds = [
    ...new Set((fixtures || []).flatMap((f) => [f.home_team_id, f.away_team_id]).filter((x) => x != null)),
  ];
  const scorerIds = [
    ...new Set(
      (fixtures || []).flatMap((f) => [
        ...Object.keys(isObj(f.home_team_scorers) ? f.home_team_scorers : {}),
        ...Object.keys(isObj(f.away_team_scorers) ? f.away_team_scorers : {}),
      ])
    ),
  ].filter((id) => /^\d+$/.test(id));

  const [teamsRes, playersRes] = await Promise.all([
    teamIds.length
      ? supabase
          .from("teams")
          .select("id, name, logo_url, wins, draws, losses, forfeits, goalsFor, goalsAgainst")
          .in("id", teamIds)
      : Promise.resolve({ data: [], error: null }),
    scorerIds.length
      ? supabase.from("players").select("id, name, jersey_number, team_id").in("id", scorerIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (teamsRes.error) throw teamsRes.error;
  if (playersRes.error) throw playersRes.error;

  const teams = (teamsRes.data || []).map((t) => ({ id: t.id, name: t.name, logo_url: t.logo_url || null }));
  return { fixtures: fixtures || [], teams, players: playersRes.data || [], official: teamsRes.data || [] };
}

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
